import { FunctionComponent, useRef } from "react";
import { useTranslation } from "react-i18next";

import { faBolt, faClock, faPowerOff } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { ListSourceType, ListStatus, ListType } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";

import { listRunFrequencyLabel } from "../utils/listScheduleLabels";

import { List } from "../schema/list";

/**
 * The list's enrichment run-state as an Activate/Deactivate hover-swap button (mockup parity):
 * "Active"/"Deactivated" at rest, swapping to "Deactivate"/"Activate" on hover. The running marker
 * is a thunder for one-off lists and a clock for recurring (ON_GOING) ones. The button is disabled
 * while the list can't run (archived, special, empty, mid-stop, errored, or still needing
 * configuration) — callers surface the actionable next step elsewhere (e.g. the processing chip).
 *
 * Shared by the List overview Enrichment column ({@link EnrichmentCell}) and the Lead List detail
 * L1 nav ({@link ListDetailStatusGroup}) so both stay visually and behaviourally identical.
 *
 * `configBlocked` (list still needs source/signals/find-person setup) is passed in rather than
 * derived here: it depends on the per-source relation arrays, which the single lean list
 * (`GET list/:id/lean`) drops — computing it off a lean `list` would wrongly report every list as
 * unconfigured and disable the button (PREC-3621). Each caller feeds it from a source that carries
 * those arrays (the overview lean rows, or the full list via `useListConfigurationStatus`).
 *
 * When the block has a reason worth stating — a list with hand-added leads and no uploaded file
 * can never run (PREC-4118) — `disabledReason` replaces the run-frequency tooltip, so the greyed
 * out control explains itself instead of being a dead button.
 */
export const ListEnrichmentToggle: FunctionComponent<{
	list: List;
	configBlocked: boolean;
	/** Shown instead of the run-frequency tooltip while the button is disabled. */
	disabledReason?: string;
	/**
	 * Its promise keeps the button from firing again while the request is in flight. The toggle does
	 * not report a rejection — the caller owns the failure message and must not rely on it throwing.
	 */
	onToggle: (id: number, status: ListStatus) => void | Promise<void>;
	/**
	 * `"default"` keeps the overview table's intrinsic height (`py-1`). `"toolbar"` pins it to the
	 * 32px `ButtonSize.XS` height so it lines up with the other controls in the detail L1 nav.
	 */
	size?: "default" | "toolbar";
}> = ({ list, configBlocked, disabledReason, onToggle, size = "default" }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.${key}`);

	const isActive = list.status === ListStatus.ACTIVE;
	const runIcon = list.type === ListType.ONCE ? faBolt : faClock;
	// A ref, not state: two clicks in the same tick must both see the first one's claim.
	const pendingToggleRef = useRef(false);
	const disabled =
		list.archived ||
		list.specialType !== null ||
		list.sourceType === ListSourceType.EMPTY ||
		list.status === ListStatus.STOPPING ||
		list.status === ListStatus.ERROR ||
		configBlocked;

	return (
		<Tooltip
			content={
				disabled && disabledReason
					? disabledReason
					: listRunFrequencyLabel(list.type, list.recurrence)
			}
		>
			<button
				className={classNames(
					"group/enrich inline-flex min-w-[112px] items-center justify-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition-colors",
					size === "toolbar" ? "h-8" : "py-1",
					isActive
						? "bg-status-success-light text-status-success border-status-success hover:bg-status-error-light hover:text-status-error hover:border-status-error"
						: "bg-gray-100 text-gray-500 border-gray-300 hover:bg-status-success-light hover:text-status-success hover:border-status-success",
					disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
				)}
				disabled={disabled}
				type="button"
				onClick={async () => {
					// Ignore a second click while this toggle's PATCH is in flight: three callers share
					// this button, and two PATCHes used to queue two runs (PREC-5068). The flag is cleared
					// in `finally`, so a rejected request re-enables the next click.
					if (pendingToggleRef.current) {
						return;
					}

					if (list.id && list.status !== ListStatus.ERROR) {
						pendingToggleRef.current = true;

						try {
							await onToggle(list.id, list.status);
						} catch {
							// The caller reports its own failure; the click still re-arms.
						} finally {
							pendingToggleRef.current = false;
						}
					}
				}}
			>
				<span className="inline-flex items-center gap-1.5 group-hover/enrich:hidden">
					<Icon className="w-3.5 h-3.5" icon={isActive ? runIcon : faPowerOff} />
					{isActive ? ts("enrichment.active") : ts("enrichment.deactivated")}
				</span>
				<span className="hidden items-center gap-1.5 group-hover/enrich:inline-flex">
					<Icon className="w-3.5 h-3.5" icon={isActive ? faPowerOff : runIcon} />
					{isActive ? ts("enrichment.deactivate") : ts("enrichment.activate")}
				</span>
			</button>
		</Tooltip>
	);
};
